import {
    StyledAgendaAppointmentEvent,
    StyledAgendaAppointmentEventContent,
    StyledAgendaAppointmentEventParagraph,
    StyledAgendaAppointmentEventStatusIndicator,
    StyledAgendaAppointmentEventSubParagraph,
    StyledAgendaAppointmentEventTitle
} from '@/features/agenda/components/agenda-appointment-event/style'
import dayjs from 'dayjs'
import {useAgendaAppointmentEventData} from '@/features/agenda/hooks/useAgendaAppointmentEventData'
import type {AgendaEventAppointment} from '@/features/agenda/types'
import {raise} from '@/utilities/helpers'
import {forwardRef, useCallback} from 'react'
import {useTranslation} from '@/translations/i18n'
import {AgendaEventSectorIcon} from '@/features/agenda/components/agenda-event-sector-icon/AgendaEventSectorIcon'
import {Monitor01Icon} from '@/components/ui/icon/Icon'
import {AgendaAppointmentEventContextMenu} from '@/features/agenda/components/agenda-appointment-event-context-menu/AgendaAppointmentEventContextMenu'
import {useDialogContext, useDialogStore} from '@ariakit/react'
import {AgendaCreateEventContextMenuDialog} from '@/features/agenda/components/agenda-appointment-event-context-menu/AgendaCreateEventContextMenuDialog'
import {ContextMenu} from '@/components/commons/context-menu/ContextMenu'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {agendaEventStatusMapToLabel} from '../../utils'

interface AgendaAppointmentEventProps {
    start: Date
    end: Date
    event: AgendaEventAppointment
    isSmall?: boolean
    onClick?: () => void
}

export const AgendaAppointmentEvent = forwardRef<HTMLDivElement, AgendaAppointmentEventProps>(
    ({start, end, event, isSmall = false, onClick}, ref) => {
        const {status} = event

        const createEventDialogStore = useDialogStore()
        const dialog = useDialogContext()

        const handleClickContextMenuItem = useCallback(
            (action: 'viewDetails' | 'addEvent') => {
                if (action == 'viewDetails') {
                    if (dialog) {
                        dialog.show()
                    }
                }

                if (action == 'addEvent') {
                    createEventDialogStore.show()
                }
            },
            [dialog, createEventDialogStore]
        )

        const renderMenu = useCallback(
            ({anchorRect}: {anchorRect: {x: number; y: number}}) => (
                <AgendaAppointmentEventContextMenu anchorRect={anchorRect} onChange={handleClickContextMenuItem} />
            ),
            [handleClickContextMenuItem]
        )

        if (!status) return raise('status cannot be undefined in the appointment event')

        return (
            <>
                <ContextMenu menu={renderMenu} style={{height: '100%'}}>
                    <StyledAgendaAppointmentEvent
                        $status={status}
                        onClick={status == 'tentative' ? undefined : onClick}
                        ref={ref}
                    >
                        <StyledAgendaAppointmentEventStatusIndicator $status={status} />
                        <StyledAgendaAppointmentEventContent direction="column">
                            {isSmall ? (
                                <SmallAgendaAppointmentEventContent start={start} end={end} event={event} />
                            ) : (
                                <RegularAgendaAppointmentEventContent start={start} end={end} event={event} />
                            )}
                        </StyledAgendaAppointmentEventContent>
                    </StyledAgendaAppointmentEvent>
                </ContextMenu>
                <AgendaCreateEventContextMenuDialog
                    start={start}
                    end={end}
                    createEventDialogStore={createEventDialogStore}
                />
            </>
        )
    }
)

const SmallAgendaAppointmentEventContent = ({
    start,
    end,
    event
}: {
    start: Date
    end: Date
    event: AgendaEventAppointment
}) => {
    const {t} = useTranslation()
    const {accessTypes, fullNames, sector} = useAgendaAppointmentEventData({agendaEvent: event})

    const content = (
        <>
            <StyledAgendaAppointmentEventTitle>{fullNames}</StyledAgendaAppointmentEventTitle>
            <StyledAgendaAppointmentEventParagraph>
                <AgendaEventSectorIcon sector={sector} size={12} />
                {accessTypes}
            </StyledAgendaAppointmentEventParagraph>
            <StyledAgendaAppointmentEventSubParagraph>
                {dayjs(start).format('HH:mm')} - {dayjs(end).format('HH:mm')}
            </StyledAgendaAppointmentEventSubParagraph>
            {event.isRemote && (
                <StyledAgendaAppointmentEventParagraph>
                    <Monitor01Icon size={12} />
                    <span>{t('accesses:remote')}</span>
                </StyledAgendaAppointmentEventParagraph>
            )}
            {event.frequency && (
                <StyledAgendaAppointmentEventSubParagraph style={{marginTop: 'auto'}}>
                    {t('agenda:recurring')}
                </StyledAgendaAppointmentEventSubParagraph>
            )}
        </>
    )

    return (
        <Tooltip content={content} variant="light" isOpenOnClickEnabled={false}>
            <StyledAgendaAppointmentEventTitle>{fullNames}</StyledAgendaAppointmentEventTitle>
        </Tooltip>
    )
}

const RegularAgendaAppointmentEventContent = ({
    start,
    end,
    event
}: {
    start: Date
    end: Date
    event: AgendaEventAppointment
}) => {
    const {t} = useTranslation()
    const {frequency} = event
    const {accessTypes, fullNames, sector} = useAgendaAppointmentEventData({agendaEvent: event})

    return (
        <>
            <StyledAgendaAppointmentEventTitle>{fullNames}</StyledAgendaAppointmentEventTitle>
            <StyledAgendaAppointmentEventParagraph>
                <AgendaEventSectorIcon sector={sector} size={12} accessTypes={accessTypes} />
                <span>{accessTypes}</span>
            </StyledAgendaAppointmentEventParagraph>
            <StyledAgendaAppointmentEventSubParagraph>
                {event.time && event.time != '00:00:00'
                    ? `${dayjs(start).format('HH:mm')} - ${dayjs(end).format('HH:mm')}`
                    : t('agenda:generated_event')}

                <br />
            </StyledAgendaAppointmentEventSubParagraph>
            {event.isRemote && (
                <StyledAgendaAppointmentEventParagraph>
                    <Monitor01Icon size={12} />
                    <span>{t('accesses:remote')}</span>
                </StyledAgendaAppointmentEventParagraph>
            )}
            <StyledAgendaAppointmentEventSubParagraph style={{fontWeight: '500'}}>
                {t(agendaEventStatusMapToLabel[event.status])}
            </StyledAgendaAppointmentEventSubParagraph>
            {frequency && (
                <StyledAgendaAppointmentEventSubParagraph style={{marginTop: 'auto'}}>
                    {t('agenda:recurring')}
                </StyledAgendaAppointmentEventSubParagraph>
            )}
        </>
    )
}
